import { Button, DatePicker } from '@consigli/facade';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DeadlineDropdownContentProps {
  onSelect: (deadline: string | null) => void;
  onCancel: () => void;
}

/**
 * Deadline picker content for the bulk "Change Deadline" popover.
 * (The individual-item editor, EditableDeadline, has its own implementation.)
 */
export const DeadlineDropdownContent: FC<DeadlineDropdownContentProps> = ({
  onSelect,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string>('');
  const inputRef = useCallback((el: HTMLInputElement | null) => {
    el?.focus();
  }, []);

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
  };

  // Commit only on explicit confirm (Save button or Enter).
  const commit = useCallback(() => {
    if (selectedDate) {
      onSelect(`${selectedDate}T00:00:00`);
    }
  }, [selectedDate, onSelect]);

  // Clear the deadline on the selected items (sets due date to none).
  const handleClear = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent closing dropdown when clicking date picker or buttons
    e.stopPropagation();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="p-3" onMouseDown={handleMouseDown}>
      <div className="flex w-34 flex-col gap-2">
        <DatePicker
          ref={inputRef}
          variant="compact"
          value={selectedDate}
          onChange={(e) => handleDateChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="!w-full"
        />
        <div className="flex gap-2">
          <Button
            secondary
            onClick={handleClear}
            className="flex-1 !rounded-full !py-1 !px-3 !text-xs"
          >
            {t('meeting-secretary.action-items.clear-deadline', 'Clear')}
          </Button>
          <Button
            primary
            onClick={commit}
            disabled={!selectedDate}
            className="flex-1 !rounded-full !py-1 !px-3 !text-xs"
          >
            {t('meeting-secretary.meetings.save', 'Save')}
          </Button>
        </div>
      </div>
    </div>
  );
};
